import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth, API } from "@/App";
import { 
  ArrowLeft, MapPin, Plus, Beer, Star, MessageSquare, Camera,
  Navigation, X, Loader2, Search, ChevronRight, Users
} from "lucide-react";
import PlaceAutocomplete from "@/components/PlaceAutocomplete";
import ImageUpload from "@/components/ImageUpload";
import axios from "axios";
import { toast } from "sonner";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const loadGoogleMapsScript = () => {
  return new Promise((resolve, reject) => {
    if (window.google && window.google.maps) {
      resolve(window.google);
      return;
    }
    const existingScript = document.getElementById("google-maps-script");
    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(window.google));
      return;
    }
    const script = document.createElement("script");
    script.id = "google-maps-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

const ExploreBars = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);

  const [loading, setLoading] = useState(true);
  const [bars, setBars] = useState([]);
  const [selectedBar, setSelectedBar] = useState(null);
  const [showAddBar, setShowAddBar] = useState(false);
  const [showBarDetail, setShowBarDetail] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [viewMode, setViewMode] = useState("map"); // map or list

  // Add bar form state
  const [newBarDescription, setNewBarDescription] = useState("");
  const [newBarPlace, setNewBarPlace] = useState(null);
  const [newBarPhotos, setNewBarPhotos] = useState([]);
  const [addingBar, setAddingBar] = useState(false);

  // Comment form state
  const [commentText, setCommentText] = useState("");
  const [commentRating, setCommentRating] = useState(0);
  const [addingComment, setAddingComment] = useState(false);
  const [barPhotos, setBarPhotos] = useState([]);
  const [addingPhotos, setAddingPhotos] = useState(false);

  // Get current location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        () => {
          // Default to San Francisco if no location
          setCurrentLocation({ lat: 37.7749, lng: -122.4194 });
        }
      );
    } else {
      setCurrentLocation({ lat: 37.7749, lng: -122.4194 });
    }
  }, []);

  // Fetch bars
  useEffect(() => {
    const fetchBars = async () => {
      try {
        const params = currentLocation 
          ? `?lat=${currentLocation.lat}&lng=${currentLocation.lng}&radius=100`
          : '';
        const response = await axios.get(`${API}/bars${params}`);
        setBars(response.data.bars);
      } catch (error) {
        console.error("Failed to fetch bars:", error);
      } finally {
        setLoading(false);
      }
    };

    if (currentLocation) {
      fetchBars();
    }
  }, [currentLocation]);

  // Initialize map
  useEffect(() => {
    if (!currentLocation || !mapRef.current || viewMode !== "map") return;

    const initMap = async () => {
      try {
        await loadGoogleMapsScript();

        const mapStyles = [
          { elementType: "geometry", stylers: [{ color: "#1d1d1d" }] },
          { elementType: "labels.text.stroke", stylers: [{ color: "#1d1d1d" }] },
          { elementType: "labels.text.fill", stylers: [{ color: "#8a8a8a" }] },
          { featureType: "road", elementType: "geometry", stylers: [{ color: "#2c2c2c" }] },
          { featureType: "water", elementType: "geometry", stylers: [{ color: "#0e0e0e" }] },
        ];

        mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
          center: currentLocation,
          zoom: 12,
          styles: mapStyles,
          disableDefaultUI: true,
          zoomControl: true,
        });

        // Add user location marker
        new window.google.maps.Marker({
          position: currentLocation,
          map: mapInstanceRef.current,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: "#00FFFF",
            fillOpacity: 1,
            strokeColor: "#fff",
            strokeWeight: 2,
          },
          title: "You are here",
        });

        // Add bar markers
        addBarMarkers();

      } catch (error) {
        console.error("Map init error:", error);
      }
    };

    initMap();
  }, [currentLocation, viewMode]);

  // Update markers when bars change
  useEffect(() => {
    if (mapInstanceRef.current && bars.length > 0) {
      addBarMarkers();
    }
  }, [bars]);

  const addBarMarkers = () => {
    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    bars.forEach(bar => {
      if (!bar.coordinates) return;

      const marker = new window.google.maps.Marker({
        position: { lat: bar.coordinates.lat, lng: bar.coordinates.lng },
        map: mapInstanceRef.current,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 12,
          fillColor: "#CD7F32",
          fillOpacity: 1,
          strokeColor: "#fff",
          strokeWeight: 2,
        },
        title: bar.name,
      });

      marker.addListener("click", () => {
        handleBarClick(bar);
      });

      markersRef.current.push(marker);
    });
  };

  const handleBarClick = async (bar) => {
    try {
      const response = await axios.get(`${API}/bars/${bar.bar_id}`);
      setSelectedBar(response.data);
      setShowBarDetail(true);
    } catch (error) {
      toast.error("Failed to load bar details");
    }
  };

  const handleAddBar = async () => {
    if (!newBarPlace) {
      toast.error("Please select a location");
      return;
    }

    setAddingBar(true);
    try {
      await axios.post(`${API}/bars`, {
        name: newBarPlace.name,
        description: newBarDescription,
        address: newBarPlace.address,
        coordinates: { lat: newBarPlace.lat, lng: newBarPlace.lng },
        photos: newBarPhotos
      });

      toast.success("Bar added to the map! 🍺");
      setShowAddBar(false);
      setNewBarDescription("");
      setNewBarPlace(null);
      setNewBarPhotos([]);

      // Refresh bars
      const response = await axios.get(`${API}/bars?lat=${currentLocation.lat}&lng=${currentLocation.lng}&radius=100`);
      setBars(response.data.bars);

    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add bar");
    } finally {
      setAddingBar(false);
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) {
      toast.error("Please enter a comment");
      return;
    }

    setAddingComment(true);
    try {
      await axios.post(`${API}/bars/${selectedBar.bar_id}/comments`, {
        text: commentText,
        rating: commentRating || null
      });

      toast.success("Comment added!");
      setCommentText("");
      setCommentRating(0);

      // Refresh bar details
      const response = await axios.get(`${API}/bars/${selectedBar.bar_id}`);
      setSelectedBar(response.data);

    } catch (error) {
      toast.error("Failed to add comment");
    } finally {
      setAddingComment(false);
    }
  };

  const handleAddPhotos = async () => {
    if (barPhotos.length === 0) {
      toast.error("Please add at least one photo");
      return;
    }

    setAddingPhotos(true);
    try {
      await axios.post(`${API}/bars/${selectedBar.bar_id}/photos`, {
        photos: barPhotos
      });

      toast.success("Photos added!");
      setBarPhotos([]);

      // Refresh bar details
      const response = await axios.get(`${API}/bars/${selectedBar.bar_id}`);
      setSelectedBar(response.data);

    } catch (error) {
      toast.error("Failed to add photos");
    } finally {
      setAddingPhotos(false);
    }
  };

  const renderStars = (rating, interactive = false, onRate = null) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => interactive && onRate && onRate(star)}
            className={`${interactive ? 'cursor-pointer hover:scale-110' : 'cursor-default'} transition-transform`}
          >
            <Star
              className={`w-5 h-5 ${star <= rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-600'}`}
            />
          </button>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="mobile-container min-h-screen flex items-center justify-center asphalt-texture">
        <Loader2 className="w-8 h-8 text-bronze animate-spin" />
      </div>
    );
  }

  return (
    <div className="mobile-container asphalt-texture min-h-screen flex flex-col">
      {/* Header */}
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full"
            onClick={() => navigate('/dashboard')}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-oswald text-xl font-bold uppercase">Dive Bars</h1>
            <p className="text-xs text-gray-500">{bars.length} dives nearby</p>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            variant={viewMode === "map" ? "default" : "ghost"}
            size="sm"
            className={viewMode === "map" ? "bg-bronze" : ""}
            onClick={() => setViewMode("map")}
          >
            <MapPin className="w-4 h-4" />
          </Button>
          <Button
            variant={viewMode === "list" ? "default" : "ghost"}
            size="sm"
            className={viewMode === "list" ? "bg-bronze" : ""}
            onClick={() => setViewMode("list")}
          >
            <Users className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Map View */}
      {viewMode === "map" && (
        <div className="flex-1 relative">
          <div ref={mapRef} className="w-full h-full min-h-[50vh]" />
          
          {/* Add Bar Button */}
          {user && (
            <Button
              className="absolute bottom-4 right-4 bg-bronze hover:bg-bronze-600 rounded-full w-14 h-14 shadow-lg"
              onClick={() => setShowAddBar(true)}
            >
              <Plus className="w-6 h-6" />
            </Button>
          )}
        </div>
      )}

      {/* List View */}
      {viewMode === "list" && (
        <div className="flex-1 p-4 overflow-auto">
          {bars.length === 0 ? (
            <div className="glass rounded-xl p-8 text-center">
              <Beer className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400">No dives found nearby</p>
              <p className="text-gray-600 text-sm mt-1">Be the first to add one!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {bars.map((bar) => (
                <div
                  key={bar.bar_id}
                  className="glass rounded-xl p-4 cursor-pointer hover:ring-1 hover:ring-bronze/50 transition-all"
                  onClick={() => handleBarClick(bar)}
                >
                  <div className="flex gap-4">
                    {bar.photos?.length > 0 ? (
                      <div className="w-20 h-20 rounded-lg overflow-hidden bg-white/5 flex-shrink-0">
                        <img src={bar.photos[0]} alt="" className="w-full h-full object-cover" />
                      </div>
                    ) : (
                      <div className="w-20 h-20 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                        <Beer className="w-8 h-8 text-bronze" />
                      </div>
                    )}
                    
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold truncate">{bar.name}</h3>
                      <p className="text-xs text-gray-500 truncate mt-1">{bar.address}</p>
                      
                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                        {bar.avg_rating && (
                          <span className="flex items-center gap-1">
                            <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                            {bar.avg_rating}
                          </span>
                        )}
                        {bar.distance && (
                          <span className="flex items-center gap-1">
                            <Navigation className="w-3 h-3" />
                            {bar.distance.toFixed(1)} mi
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {bar.visit_count || 0}
                        </span>
                      </div>
                    </div>
                    
                    <ChevronRight className="w-5 h-5 text-gray-600 flex-shrink-0 self-center" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add Bar Button */}
          {user && (
            <Button
              className="w-full mt-4 h-12 bg-bronze hover:bg-bronze-600 font-oswald uppercase"
              onClick={() => setShowAddBar(true)}
            >
              <Plus className="w-5 h-5 mr-2" />
              Add a Dive Bar
            </Button>
          )}
        </div>
      )}

      {/* Add Bar Modal */}
      <Dialog open={showAddBar} onOpenChange={setShowAddBar}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-sm mx-4 max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-oswald uppercase flex items-center gap-2">
              <Beer className="w-5 h-5 text-bronze" />
              Add Dive Bar
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <label className="text-xs text-gray-400 uppercase flex items-center gap-2">
                <Search className="w-3 h-3" />
                Search Location
              </label>
              <PlaceAutocomplete
                onPlaceSelect={(place) => setNewBarPlace(place)}
                placeholder="Search for the bar..."
              />
              {newBarPlace && (
                <div className="p-3 bg-bronze/10 rounded-lg border border-bronze/30">
                  <p className="font-semibold text-white">{newBarPlace.name}</p>
                  <p className="text-xs text-gray-400 mt-1">{newBarPlace.address}</p>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-xs text-gray-400 uppercase">Description</label>
              <Textarea
                value={newBarDescription}
                onChange={(e) => setNewBarDescription(e.target.value)}
                placeholder="What makes this place special? Best jukebox, cheap drinks, friendly locals..."
                className="bg-[#121212] border-white/10 min-h-[80px]"
              />
            </div>

            <div className="space-y-2">
              <label className="text-xs text-gray-400 uppercase flex items-center gap-2">
                <Camera className="w-3 h-3" />
                Photos
              </label>
              <ImageUpload
                images={newBarPhotos}
                onImagesChange={setNewBarPhotos}
                maxImages={5}
                folder="bars"
              />
            </div>

            <Button
              className="w-full h-12 bg-bronze hover:bg-bronze-600 font-oswald uppercase"
              onClick={handleAddBar}
              disabled={addingBar}
            >
              {addingBar ? <Loader2 className="w-5 h-5 animate-spin" /> : "Add Bar"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bar Detail Modal */}
      <Dialog open={showBarDetail} onOpenChange={setShowBarDetail}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-sm mx-4 max-h-[90vh] overflow-y-auto p-0">
          {selectedBar && (
            <>
              {/* Photos */}
              {selectedBar.photos?.length > 0 ? (
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={selectedBar.photos[0]}
                    alt={selectedBar.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
                  {selectedBar.photos.length > 1 && (
                    <div className="absolute bottom-2 right-2 bg-black/60 px-2 py-1 rounded text-xs">
                      +{selectedBar.photos.length - 1} more
                    </div>
                  )}
                </div>
              ) : (
                <div className="h-32 bg-gradient-to-b from-bronze/20 to-transparent flex items-center justify-center">
                  <Beer className="w-16 h-16 text-bronze/50" />
                </div>
              )}

              <div className="p-4 space-y-4">
                {/* Header */}
                <div>
                  <h2 className="font-oswald text-2xl font-bold">{selectedBar.name}</h2>
                  <p className="text-sm text-gray-400 mt-1">{selectedBar.address}</p>
                  
                  <div className="flex items-center gap-4 mt-3">
                    {selectedBar.avg_rating && (
                      <div className="flex items-center gap-1">
                        {renderStars(Math.round(selectedBar.avg_rating))}
                        <span className="text-sm text-gray-400 ml-1">({selectedBar.comment_count})</span>
                      </div>
                    )}
                    <span className="text-sm text-gray-500 flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {selectedBar.visit_count || 0} visits
                    </span>
                  </div>
                </div>

                {/* Description */}
                {selectedBar.description && (
                  <p className="text-gray-300 text-sm">{selectedBar.description}</p>
                )}

                {/* Photo Gallery */}
                {selectedBar.photos?.length > 1 && (
                  <div>
                    <h3 className="text-xs text-gray-400 uppercase mb-2">Photos</h3>
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {selectedBar.photos.map((photo, idx) => (
                        <img
                          key={idx}
                          src={photo}
                          alt=""
                          className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Add Photos */}
                {user && (
                  <div className="space-y-2">
                    <h3 className="text-xs text-gray-400 uppercase">Add Your Photos</h3>
                    <ImageUpload
                      images={barPhotos}
                      onImagesChange={setBarPhotos}
                      maxImages={5}
                      folder={`bars/${selectedBar.bar_id}`}
                    />
                    {barPhotos.length > 0 && (
                      <Button
                        size="sm"
                        className="bg-bronze hover:bg-bronze-600"
                        onClick={handleAddPhotos}
                        disabled={addingPhotos}
                      >
                        {addingPhotos ? <Loader2 className="w-4 h-4 animate-spin" /> : "Upload Photos"}
                      </Button>
                    )}
                  </div>
                )}

                {/* Comments */}
                <div>
                  <h3 className="text-xs text-gray-400 uppercase mb-3 flex items-center gap-2">
                    <MessageSquare className="w-3 h-3" />
                    Reviews ({selectedBar.comment_count || 0})
                  </h3>

                  {/* Add Comment */}
                  {user && (
                    <div className="glass rounded-lg p-3 mb-4 space-y-3">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Your Rating</p>
                        {renderStars(commentRating, true, setCommentRating)}
                      </div>
                      <Textarea
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        placeholder="Share your experience..."
                        className="bg-[#0a0a0a] border-white/10 min-h-[60px]"
                      />
                      <Button
                        size="sm"
                        className="bg-bronze hover:bg-bronze-600"
                        onClick={handleAddComment}
                        disabled={addingComment}
                      >
                        {addingComment ? <Loader2 className="w-4 h-4 animate-spin" /> : "Post Review"}
                      </Button>
                    </div>
                  )}

                  {/* Comments List */}
                  <div className="space-y-3">
                    {selectedBar.comments?.map((comment) => (
                      <div key={comment.comment_id} className="border-b border-white/5 pb-3">
                        <div className="flex items-center gap-2 mb-2">
                          {comment.user_picture ? (
                            <img src={comment.user_picture} alt="" className="w-6 h-6 rounded-full" />
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-bronze/20 flex items-center justify-center">
                              <span className="text-xs text-bronze">{comment.user_name?.[0]}</span>
                            </div>
                          )}
                          <span className="text-sm font-medium">{comment.user_name}</span>
                          {comment.rating && (
                            <div className="flex ml-auto">
                              {renderStars(comment.rating)}
                            </div>
                          )}
                        </div>
                        <p className="text-sm text-gray-300">{comment.text}</p>
                      </div>
                    ))}

                    {(!selectedBar.comments || selectedBar.comments.length === 0) && (
                      <p className="text-gray-500 text-sm text-center py-4">
                        No reviews yet. Be the first!
                      </p>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    className="flex-1 border-white/20"
                    onClick={() => {
                      const url = `https://www.google.com/maps/dir/?api=1&destination=${selectedBar.coordinates.lat},${selectedBar.coordinates.lng}`;
                      window.open(url, '_blank');
                    }}
                  >
                    <Navigation className="w-4 h-4 mr-2" />
                    Directions
                  </Button>
                  <Button
                    className="flex-1 bg-bronze hover:bg-bronze-600"
                    onClick={() => setShowBarDetail(false)}
                  >
                    Close
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExploreBars;
